import {
  RadialBarChart as RechartsRadialBarChart,
  RadialBar,
  Legend,
  Tooltip,
  PolarAngleAxis,
} from 'recharts';
import { ChartContainer } from './ChartContainer';
import { ChartTooltipContent } from './ChartTooltip';
import type { ChartConfig } from './chart-utils';
import { CHART_COLORS } from './chart-utils';

interface RadialChartProps {
  data: Array<{ name: string; value: number; fill?: string; [key: string]: unknown }>;
  config: ChartConfig;
  height?: number;
  unit?: string;
  className?: string;
  ariaLabel?: string;
  maxValue?: number;
  startAngle?: number;
  endAngle?: number;
  /** Erzwungene Nachkommastellen im Tooltip. `undefined` = auto (0–2). */
  decimals?: number;
}

/**
 * RadialChart - Kreisfoermiges Balkendiagramm.
 *
 * @example Einfach (z.B. Zielerreichung):
 * data={[
 *   { name: 'strom', value: 78, fill: 'oklch(60% 0.2 260)' },
 *   { name: 'gas', value: 65, fill: 'oklch(60% 0.18 145)' },
 * ]}
 * config={{ strom: { label: 'Strom', color: '...' }, gas: { label: 'Gas', color: '...' } }}
 *
 * @example Genestete Daten (mehrere Ringe = Kategorien):
 * data={[
 *   { name: 'zuverlaessigkeit', value: 92 },
 *   { name: 'zufriedenheit', value: 87 },
 *   { name: 'effizienz', value: 74 },
 * ]}
 */
export function CPRadialChart({
  data,
  config,
  height = 350,
  unit,
  className,
  ariaLabel,
  maxValue = 100,
  startAngle = 90,
  endAngle = -270,
  decimals,
}: RadialChartProps) {
  const chartData = data.map((item, i) => ({
    ...item,
    fill: item.fill ?? config[item.name]?.color ?? CHART_COLORS[i % CHART_COLORS.length],
  }));

  return (
    <ChartContainer config={config} height={height} className={className} ariaLabel={ariaLabel}>
      <RechartsRadialBarChart
        data={chartData}
        cx="50%"
        cy="50%"
        innerRadius="20%"
        outerRadius="90%"
        startAngle={startAngle}
        endAngle={endAngle}
        barSize={16}
      >
        <PolarAngleAxis type="number" domain={[0, maxValue]} tick={false} angleAxisId={0} />
        <RadialBar
          dataKey="value"
          background={{ fill: 'var(--color-brand-surface-alt, #f3f4f6)' }}
          cornerRadius={8}
        />
        <Tooltip content={<ChartTooltipContent config={config} unit={unit} decimals={decimals} />} />
        <Legend
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: '13px' }}
          payload={chartData.map((entry) => ({
            value: config[entry.name]?.label ?? entry.name,
            type: 'circle' as const,
            color: entry.fill,
          }))}
        />
      </RechartsRadialBarChart>
    </ChartContainer>
  );
}
